/**
 * Legacy template font names (Aimbrill-*) map to loadable Google families.
 * Templates store Aimbrill-Poppins; preview must load Poppins from Google Fonts.
 */
export const LEGACY_FONT_TO_GOOGLE = {
  "Aimbrill-Poppins": "Poppins",
  "Aimbrill-Domine": "Domine",
  "Aimbrill-Inter": "Inter",
};

/**
 * Stored in section customCSS when the merchant wants the Shopify theme font.
 * Renderers must omit font-family so the storefront theme cascades through.
 */
export const THEME_DEFAULT_FONT = "theme-default";

const OMIT_BLOCK_FONT_VALUES = new Set([
  "",
  "inherit",
  THEME_DEFAULT_FONT,
  "theme",
  "default",
  "store-theme",
]);

/** Built-in @font-face for legacy Aimbrill-* names (maps to Google gstatic files). */
export const BUILTIN_LEGACY_FONT_FACES = [
  {
    family: "Aimbrill-Poppins",
    url: "https://fonts.gstatic.com/s/poppins/v22/pxiEyp8kv8JHgFVrJJfecg.woff2",
    format: "woff2",
  },
  {
    family: "Aimbrill-Domine",
    url: "https://fonts.gstatic.com/s/domine/v25/L0xhDFMnlVwD4h3Lt9JWnbX3jG-2X3LAI10.ttf",
    format: "truetype",
  },
  {
    family: "Aimbrill-Inter",
    url: "https://fonts.gstatic.com/s/inter/v20/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuLyfMZg.ttf",
    format: "truetype",
  },
];

export function isThemeDefaultFontFamily(fontFamily) {
  const normalized = String(fontFamily ?? "")
    .trim()
    .toLowerCase();
  return OMIT_BLOCK_FONT_VALUES.has(normalized) && normalized !== "inherit";
}

export function shouldOmitBlockFontFamily(fontFamily) {
  const normalized = String(fontFamily ?? "")
    .trim()
    .toLowerCase();
  if (!normalized) return true;
  return OMIT_BLOCK_FONT_VALUES.has(normalized);
}

/**
 * Read the active Shopify theme font from the host page (storefront only).
 * Editor / preview iframes fall back to omitting font-family.
 */
export function resolveStorefrontThemeFontFamily() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return undefined;
  }

  try {
    const candidates = [
      document.body,
      document.documentElement,
      document.querySelector("main"),
      document.querySelector(".shopify-section"),
      document.querySelector("#MainContent"),
    ].filter(Boolean);

    for (const node of candidates) {
      const family = window.getComputedStyle(node).fontFamily;
      if (!family || family === "inherit") continue;
      const first = family.split(",")[0].replace(/['"]/g, "").trim();
      if (!first || /^(serif|sans-serif|monospace|cursive|fantasy|system-ui)$/i.test(first)) {
        continue;
      }
      return family;
    }
  } catch {
    return undefined;
  }

  return undefined;
}

export function resolveFontFamilyForStylesheet(name) {
  if (shouldOmitBlockFontFamily(name)) return null;
  const first = String(name || "")
    .split(",")[0]
    .replace(/["']/g, "")
    .trim();
  if (!first) return null;
  return LEGACY_FONT_TO_GOOGLE[first] || first;
}

/**
 * Resolve editor/template fontFamily for React inline styles + Google font loading.
 * Returns undefined when font-family should not be set on the element.
 */
export function resolveLegacyFontCssFamily(fontFamily) {
  if (fontFamily == null || String(fontFamily).trim() === "" || fontFamily === "inherit") {
    return undefined;
  }

  if (isThemeDefaultFontFamily(fontFamily)) {
    return resolveStorefrontThemeFontFamily();
  }

  const cleaned = String(fontFamily).trim().replace(/^["']|["']$/g, "");
  if (!cleaned) return undefined;

  if (/^(sans-serif|serif|monospace|cursive|fantasy|system-ui)$/i.test(cleaned)) {
    return cleaned.toLowerCase();
  }

  const firstFamily = cleaned.includes(",")
    ? cleaned.split(",")[0].trim().replace(/^["']|["']$/g, "")
    : cleaned;
  const resolved = LEGACY_FONT_TO_GOOGLE[firstFamily] || firstFamily;

  if (cleaned.includes(",") && !LEGACY_FONT_TO_GOOGLE[firstFamily]) {
    return cleaned;
  }

  return `"${resolved}", sans-serif`;
}

/** Inline style patch: `{ fontFamily }` or `{}`. */
export function buildFontFamilyStyle(fontFamily) {
  const resolved = resolveLegacyFontCssFamily(fontFamily);
  return resolved ? { fontFamily: resolved } : {};
}

/** CSS custom property patch: `{ "--var-name": value }` or `{}`. */
export function buildFontFamilyCssVar(cssVarName, fontFamily) {
  const resolved = resolveLegacyFontCssFamily(fontFamily);
  return resolved ? { [cssVarName]: resolved } : {};
}

/** Strip or resolve block customCSS fontFamily before inline styles / CSS vars. */
export function resolveBlockCssFontFamily(css = {}) {
  if (!css || typeof css !== "object") {
    return css ?? {};
  }
  const { fontFamily, ...rest } = css;
  const resolved = resolveLegacyFontCssFamily(fontFamily);
  if (!resolved) {
    return rest;
  }
  return { ...rest, fontFamily: resolved };
}
